<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('stockReturn.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('stockReturn.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="d-flex justify-content-between">
          <div class="ts-flex ts-items-center ts-space-x-3">
            <ts-button outline color="info" @click.prevent="addNew">
              {{ $t('add_new') }}
            </ts-button>
            <a-range-picker
              v-model="dateRange"
              :allowClear="false"
              format="DD-MM-YYYY"
              valueFormat="DD-MM-YYYY"
              style="width:200px"
              @change="fetchData()"
            />
          </div>
          <div class="d-flex ts-space-x-3 ts-items-center">
            <ts-input
              v-model="search"
              v-tooltip="$t('stockReturn.search')"
              :placeholder="$t('search')"
              style="width: 250px;"
              class="inline-block"
            />
            <ts-filter ref="filter">
              <div class="row">
                <div class="col-md-12">
                  <label class="">{{ $t('stockReturn.branch') }}</label>
                  <ts-branch-filter
                    @filter="branchFilter"
                    class="ts-w-full"
                  ></ts-branch-filter>
                </div>
              </div>
            </ts-filter>
          </div>
        </div>
      </ts-card-wrapper>
      <div class="overflow-auto">
        <ts-table :columns="columns" :records="resources" :loading="loading">
          <template v-slot="{ record }">
            <td class="ts-whitespace-no-wrap">{{ record.branch_name_en }}</td>
            <td class="ts-whitespace-no-wrap">{{ record.return_date }}</td>
            <td class="ts-whitespace-no-wrap">{{ record.reference_no }}</td>
            <td class="ts-whitespace-no-wrap">{{ record.return_number }}</td>
            <td class="ts-whitespace-no-wrap">{{ record.customer_name }}</td>
            <td class="ts-whitespace-no-wrap">{{ record.location_name_en }}</td>
            <td class="ts-whitespace-no-wrap">{{ record.returned_name }}</td>
            <td class="ts-whitespace-no-wrap ts-text-right">
              {{ $currencyFormat(record.total_amount) }}
            </td>
            <td class="text-center ts-space-x-3">
              <a
                href="#"
                v-tooltip="$t('edit')"
                class="text-primary"
                @click.prevent="edit(record)"
              >
                <i class="fas fa-edit"></i>
              </a>
              <a-popconfirm
                placement="leftTop"
                :title="$t('are_you_sure_to_delete')"
                :ok-text="$t('yes')"
                :cancel-text="$t('no')"
                @confirm="deleteRecord(record)"
              >
                <a href="#" :disabled="record._deleting">
                  <i
                    class="fas fa-circle-notch spin"
                    v-if="record._deleting"
                  ></i>
                  <i class="fa fa-trash text-red" v-else></i>
                </a>
              </a-popconfirm>
            </td>
          </template>
        </ts-table>
      </div>
      <div class="d-flex justify-content-end p-15">
        <ts-pagination v-model="pagination" @navigate="fetchData">
        </ts-pagination>
      </div>
    </ts-card>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapState } from 'vuex'
import { Errors } from 'form-backend-validation'
import moment from 'moment'
import Reason from './components/reason_delete'

export default {
  name: 'stockReturn',
  data() {
    return {
      loading: false,
      errors: new Errors(),
      model: {
        deleted_reason: null
      }
    }
  },
  created() {
    this.$store.state.stock.stockReturn.dateRange = [
      moment()
        .add(-7, 'days')
        .format('DD-MM-YYYY'),
      moment().format('DD-MM-YYYY')
    ]
    this.fetchData()
  },
  computed: {
    ...mapState('stock/stockReturn', ['resources', 'pagination']),
    search: {
      get() {
        return this.$store.state.stock.stockReturn.search
      },
      set(newValue) {
        this.$store.commit('stock/stockReturn/SET_SEARCH', newValue)
        this.$store.commit('stock/stockReturn/RESET_CURRENT_PAGE')
      }
    },
    inactive: {
      get() {
        return this.$store.state.stock.stockReturn.isInActive
      },
      set(newValue) {
        this.$store.commit('stock/stockReturn/SET_IS_INACTIVE', newValue)
      }
    },
    dateRange: {
      get() {
        return this.$store.state.stock.stockReturn.dateRange
      },
      set(newValue) {
        this.$store.commit('stock/stockReturn/SET_DATE_RANGE', newValue)
      }
    },
    columns() {
      return [
        { name: this.$t('stockReturn.branch') },
        { name: this.$t('stockReturn.date') },
        { name: this.$t('stockReturn.reference') },
        { name: this.$t('stockReturn.stockReturnNumber') },
        { name: this.$t('stockReturn.customer') },
        { name: this.$t('stockReturn.location') },
        { name: this.$t('stockReturn.returnBy') },
        { name: this.$t('stockReturn.totalAmount'), style: 'text-align:right' },
        {
          name: this.$t('actions'),
          style: 'text-align:center; width:7%'
        }
      ]
    }
  },
  methods: {
    addNew() {
      this.$router.push({ name: 'stock-return-create' })
    },
    fetchData(attributes) {
      this.loading = true
      this.$store
        .dispatch('stock/stockReturn/fetch', { ...attributes })
        .then(() => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    edit(record) {
      this.$store.commit('stock/stockReturn/SET_EDIT_DATA', record)
      this.$router.push({
        name: 'stock-return-edit',
        params: { id: record.return_id }
      })
    },
    deleteRecord(record) {
      this.model.deleted_reason = null
      this.$confirm({
        title: this.$t('are_you_sure_to_delete'),
        content: h =>
          h(Reason, {
            props: {
              value: this.model,
              validate: this.errors,
              placeholder: this.$t('stockReturn.leaveReason')
            }
          }),
        okText: this.$t('yes'),
        okType: 'danger',
        cancelText: this.$t('no'),
        onOk: () => {
          return this.$store
            .dispatch('stock/stockReturn/destroy', {
              id: record.return_id,
              data: this.model
            })
            .then(response => {
              this.$toasted.success(response.message)
              this.fetchData()
              return Promise.resolve(response)
            })
            .catch(error => {
              this.$toasted.error(error.message)
              this.errors = new Errors(error.errors)
              return Promise.reject(error)
            })
        },
        onCancel() {}
      })
    },
    branchFilter(value) {
      this.$store.commit('stock/stockReturn/SET_BRANCH_ID', value)
      this.fetchData()
    }
  },
  watch: {
    search: debounce(function() {
      this.fetchData()
    }, 500)
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('stock/stockReturn/RESET_STATE')
    next()
  }
}
</script>
